/**
 * Theme Font Loader
 *
 * The guest PWA applies the gallery's selected font as a CSS variable, but the
 * font file itself must be requested explicitly. This module mirrors the
 * families offered by the portal's ThemePicker (see FONT_OPTIONS in
 * wedding-customer-portal-frontend/src/components/ThemePicker.js) and injects
 * the matching Google Fonts stylesheet once per family.
 *
 * Unknown families (including system stacks and generics) are skipped: only
 * these known Google Fonts are ever requested.
 */

// A Map (not a plain object) so inherited keys like `constructor` or
// `__proto__` can never resolve to a truthy query.
const GOOGLE_FONT_QUERIES = new Map([
  ['playfair display', 'Playfair+Display:wght@400;600;700'],
  ['montserrat', 'Montserrat:wght@400;500;600;700'],
  ['lora', 'Lora:wght@400;500;600;700'],
  ['raleway', 'Raleway:wght@400;500;600;700'],
  ['cormorant garamond', 'Cormorant+Garamond:wght@400;500;600;700'],
  ['open sans', 'Open+Sans:wght@400;500;600;700'],
  ['crimson text', 'Crimson+Text:wght@400;600;700'],
]);

function firstFontFamily(fontStack) {
  if (typeof fontStack !== 'string') {
    return '';
  }
  const [first = ''] = fontStack.split(',');
  return first.trim().replace(/^["']+|["']+$/g, '').trim();
}

function fontSlug(family) {
  return family
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Ensure the stylesheet for a theme font stack is present in the document.
 * Idempotent: each known family is requested at most once per document.
 *
 * @param {string|null|undefined} fontStack - CSS font-family value, e.g. "'Lora', Georgia, serif"
 */
export const ensureThemeFontLoaded = (fontStack) => {
  const family = firstFontFamily(fontStack);
  if (!family) {
    return;
  }
  const query = GOOGLE_FONT_QUERIES.get(family.toLowerCase());
  if (!query) {
    return;
  }
  const id = `google-font-${fontSlug(family)}`;
  if (document.getElementById(id)) {
    return;
  }
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${query}&display=swap`;
  link.setAttribute('data-theme-font', family);
  document.head.appendChild(link);
};
